import _class_call_check from "@swc/helpers/src/_class_call_check.mjs";
import _create_class from "@swc/helpers/src/_create_class.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import { Debug } from "@core/debug";
import { every } from "underscore";
import { PrivilegeDomains, PrivilegeLevels, PrivilegeOwners, PrivilegesConstants, PrivilegeTypes } from "../../privileges/constants/PrivilegesConstants.js";
import { PrivilegeUtils } from "../../privileges/utils/PrivilegeUtils.js";
export var UserPrivilegesValidator = /*#__PURE__*/ function() {
    "use strict";
    function UserPrivilegesValidator(privileges) {
        _class_call_check(this, UserPrivilegesValidator);
        this.privileges = privileges;
        var overrides = Debug.getState("privilegesTool");
        if (overrides) {
            var ref;
            this.privileges = this.privileges.filter(function(privilege) {
                return (ref = overrides[JSON.stringify(privilege)]) !== null && ref !== void 0 ? ref : true;
            });
        }
    }
    var _proto = UserPrivilegesValidator.prototype;
    _proto.hasPrivilege = function hasPrivilege(privilege) {
        return PrivilegeUtils.hasPrivilege(this.privileges, privilege);
    };
    _proto.canViewGroupsForTargetDomain = function canViewGroupsForTargetDomain(targetDomain) {
        return this.hasPrivilege({
            owner: PrivilegeOwners.Platform,
            targetDomain: targetDomain,
            type: PrivilegeTypes.View
        });
    };
    _proto.hasWritePrivilege = function hasWritePrivilege(privilege) {
        return this.hasPrivilege(privilege);
    };
    _proto.hasAllPrivileges = function hasAllPrivileges() {
        var privileges = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : [];
        return PrivilegeUtils.hasAllPrivileges(this.privileges, privileges);
    };
    _proto.hasAllViewPrivileges = function hasAllViewPrivileges() {
        var privileges = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : [];
        var _this = this;
        return every(privileges, function(privilege) {
            return privilege.type ? _this.hasPrivilege(_object_spread_props(_object_spread({}, privilege), {
                type: PrivilegeTypes.View
            })) || _this.hasPrivilege(_object_spread_props(_object_spread({}, privilege), {
                type: PrivilegeTypes.Allow
            })) : _this.hasPrivilege(privilege);
        });
    };
    _proto.canEditGroup = function canEditGroup(targetId) {
        return targetId ? this.hasWritePrivilege({
            owner: PrivilegeOwners.Platform,
            targetDomain: PrivilegeDomains.Group,
            type: PrivilegeTypes.Edit,
            targetId: targetId
        }) : this.canEditAllGroups;
    };
    _proto.canEditSource = function canEditSource(targetId) {
        return targetId ? this.hasWritePrivilege({
            owner: PrivilegeOwners.Platform,
            targetDomain: PrivilegeDomains.Source,
            type: PrivilegeTypes.Edit,
            targetId: targetId
        }) : this.canEditAllSources;
    };
    _proto.canAllowPushDocumentOneSources = function canAllowPushDocumentOneSources(targetId) {
        return targetId ? this.hasWritePrivilege({
            owner: PrivilegeOwners.Platform,
            targetDomain: PrivilegeDomains.PushDocument,
            targetId: targetId
        }) : this.canAllowPushDocumentAllSources;
    };
    _proto.canCreateAPIKeyFromSource = function canCreateAPIKeyFromSource(useSecuredVisibility, targetId) {
        var initialPrivilege = this.hasWritePrivilege({
            owner: PrivilegeOwners.Platform,
            targetDomain: PrivilegeDomains.ApiKey,
            type: PrivilegeTypes.Create
        }) && this.canAllowPushDocumentOneSources(targetId);
        if (useSecuredVisibility) {
            return initialPrivilege && this.canAllowPushIdentityAllProviders && this.canViewSecurityProviders;
        }
        return initialPrivilege;
    };
    _proto.canEditApiKey = function canEditApiKey(targetId) {
        return targetId ? this.hasWritePrivilege({
            owner: PrivilegeOwners.Platform,
            targetDomain: PrivilegeDomains.ApiKey,
            type: PrivilegeTypes.Edit,
            targetId: targetId
        }) : this.canEditAllApiKeys;
    };
    _proto.canEditExtension = function canEditExtension(targetId) {
        return targetId ? this.hasWritePrivilege({
            owner: PrivilegeOwners.Platform,
            targetDomain: PrivilegeDomains.Extensions,
            type: PrivilegeTypes.Edit,
            targetId: targetId
        }) : this.canEditAllExtensions;
    };
    _proto.canEditPipeline = function canEditPipeline(targetId) {
        return targetId ? this.hasWritePrivilege({
            owner: PrivilegeOwners.SearchApi,
            targetDomain: PrivilegeDomains.QueryPipeline,
            type: PrivilegeTypes.Edit,
            targetId: targetId
        }) : this.canEditAllPipelines;
    };
    _proto.canEditPipelineCondition = function canEditPipelineCondition(targetId) {
        return this.canEditPipeline(targetId) && this.hasPrivilege({
            owner: PrivilegeOwners.UsageAnalytics,
            targetDomain: PrivilegeDomains.AnalyticsData,
            type: PrivilegeTypes.View
        }) && this.hasPrivilege({
            owner: PrivilegeOwners.UsageAnalytics,
            targetDomain: PrivilegeDomains.CustomDimensions,
            type: PrivilegeTypes.View
        }) || this.canEditAllConditions;
    };
    _proto.canViewProjectByResource = function canViewProjectByResource(resource, license) {
        var ref, ref1;
        var mapResourceTypeToPrivilege = {
            CATALOG: this.canViewCatalogs,
            CASE_ASSIST: this.canViewCaseAssist,
            CRAWLING_MODULE: Boolean((ref = license.properties) === null || ref === void 0 ? void 0 : (ref1 = ref.content) === null || ref1 === void 0 ? void 0 : ref1.onPremisesSourcesEnabled) && this.canViewSources && this.canViewCrawlingModule,
            EXTENSION: this.canViewExtensions,
            IN_PRODUCT_EXPERIENCE: this.canViewSearchPages,
            INSIGHT_PANEL: this.canViewInsightPanel,
            ML_MODEL: this.canViewPipelines && this.canViewMachineLearningModels,
            QUERY_PIPELINE: this.canViewPipelines,
            SEARCH_HUB: this.canViewSearchPages && this.canViewSearchHubs,
            SEARCH_PAGE: this.canViewSearchPages,
            SECURITY_PROVIDER: this.canViewSecurityProviders,
            SOURCE: this.canViewSources,
            UA_REPORT: this.canSeeReports,
            STOREFRONT_ASSOCIATION: this.canViewCatalogs,
            FIELD: this.canViewFields,
            TRACKING_ID: this.canViewProperties
        };
        return this.hasPrivilege({
            owner: PrivilegeOwners.Platform,
            targetDomain: PrivilegeDomains.Project,
            type: PrivilegeTypes.View
        }) && mapResourceTypeToPrivilege[resource];
    };
    _proto.canEditProjectByResource = function canEditProjectByResource(resource, license) {
        return this.canViewProjectByResource(resource, license) && this.hasPrivilege({
            owner: PrivilegeOwners.Platform,
            targetDomain: PrivilegeDomains.Project,
            type: PrivilegeTypes.Edit
        });
    };
    _proto.canEditMerchandisingHub = function canEditMerchandisingHub(targetId) {
        return targetId ? this.hasPrivilege({
            owner: PrivilegeOwners.Commerce,
            targetDomain: PrivilegeDomains.MerchandisingHub,
            type: PrivilegeTypes.Edit,
            targetId: targetId
        }) : this.canEditAllMerchandisingHub;
    };
    _create_class(UserPrivilegesValidator, [
        {
            key: "canEditLicense",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ManageLicense
                });
            }
        },
        {
            key: "canManagePlatformFeatureModifiers",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ManagePlatformFeatureModifiers
                });
            }
        },
        {
            key: "canEditCustomerLicense",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ManageCustomerLicense
                });
            }
        },
        {
            key: "canEditCustomerLicenseOverrides",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ManageCustomerLicenseOverrides
                });
            }
        },
        {
            key: "isRankingScriptEditor",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.QueryPipelineScriptEditor,
                    type: ""
                });
            }
        },
        {
            key: "isUaManager",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.Reports,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canSeeNamedFilters",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.CustomDimensions,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.NamedFilters,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditNamedFilters",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.NamedFilters,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canSeePermissionFilters",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.CustomDimensions,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.PermissionFilters,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditPermissionFilters",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.PermissionFilters,
                    type: PrivilegeTypes.Edit
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Group,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditDimensions",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.CustomDimensions,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canEditExports",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.Exports,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canEditMetricAlerts",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.MetricAlerts,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canEditGroups",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Group,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canCreateGroups",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Group,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canEditAllGroups",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Group,
                    type: PrivilegeTypes.Edit,
                    targetId: PrivilegesConstants.allTargetIds
                });
            }
        },
        {
            key: "canViewGroups",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Group,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewRealms",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.SAMLIdentityProvider,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewOrganization",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Organization,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditOrganization",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Organization,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canEditOrganizationAdministration",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.OrganizationAdministration,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewOrganizationAdministration",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.OrganizationAdministration,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditGlobalGroups",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.GlobalGroup,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewGlobalGroup",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.GlobalGroup,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canCreateTemporaryAccessRequest",
            get: function get() {
                return this.hasPrivilege({
                    level: PrivilegeLevels.Global,
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.TemporaryAccessRequest,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canEditTemporaryAccessRequest",
            get: function get() {
                return this.hasPrivilege({
                    level: PrivilegeLevels.Global,
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.TemporaryAccessRequest,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewTemporaryAccessRequest",
            get: function get() {
                return this.hasPrivilege({
                    level: PrivilegeLevels.Global,
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.TemporaryAccessRequest,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewCrawlingModule",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.CrawlingModules,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditCrawlingModule",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.CrawlingModules,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewVisitBrowser",
            get: function get() {
                return this.hasAllPrivileges([
                    {
                        owner: PrivilegeOwners.UsageAnalytics,
                        targetDomain: PrivilegeDomains.AnalyticsData,
                        type: PrivilegeTypes.View
                    },
                    {
                        owner: PrivilegeOwners.UsageAnalytics,
                        targetDomain: PrivilegeDomains.PermissionFilters,
                        type: PrivilegeTypes.View
                    },
                    {
                        owner: PrivilegeOwners.UsageAnalytics,
                        targetDomain: PrivilegeDomains.CustomDimensions,
                        type: PrivilegeTypes.View
                    },
                    {
                        owner: PrivilegeOwners.UsageAnalytics,
                        targetDomain: PrivilegeDomains.NamedFilters,
                        type: PrivilegeTypes.View
                    }
                ]);
            }
        },
        {
            key: "canSeeReports",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.AnalyticsData,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.NamedFilters,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.Reports,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.CustomDimensions,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canSeeAllReports",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.ViewAllReports,
                    targetId: PrivilegesConstants.allTargetIds
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.AnalyticsData,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.NamedFilters,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.CustomDimensions,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditReports",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.Reports,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canAdministrateUA",
            get: function get() {
                return this.hasPrivilege({
                    targetDomain: PrivilegeDomains.Administrate,
                    owner: PrivilegeOwners.UsageAnalytics
                });
            }
        },
        {
            key: "canViewCustomDimensions",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.CustomDimensions,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewDimensions",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.CustomDimensions,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.AnalyticsData,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewDataHealth",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.CustomDimensions,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.AnalyticsData,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewExports",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.Exports,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.AnalyticsData,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewIndexes",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Index,
                    type: PrivilegeTypes.View
                }) || this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ElasticIndex,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditIndexes",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Index,
                    type: PrivilegeTypes.Edit
                }) || this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ElasticIndex,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewSecurityCache",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.SecurityCache,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditSecurityCache",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.SecurityCache,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewSecurityProviders",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.SecurityProvider,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditSecurityProviders",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.SecurityProvider,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewSources",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Source,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditSources",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Source,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canCreateSources",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Source,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canEditAllSources",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Source,
                    type: PrivilegeTypes.Edit,
                    targetId: PrivilegesConstants.allTargetIds
                });
            }
        },
        {
            key: "canAllowPushDocumentAllSources",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.PushDocument,
                    targetId: PrivilegesConstants.allTargetIds
                });
            }
        },
        {
            key: "canAllowPushIdentityAllProviders",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.PushIdentity,
                    targetId: PrivilegesConstants.allTargetIds
                });
            }
        },
        {
            key: "canViewApiKeys",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ApiKey,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canCreateApiKeys",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ApiKey,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canEditApiKeys",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ApiKey,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canEditAllApiKeys",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ApiKey,
                    type: PrivilegeTypes.Edit,
                    targetId: PrivilegesConstants.allTargetIds
                });
            }
        },
        {
            key: "canViewOrganizationsExport",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.OrganizationsExport,
                    type: PrivilegeTypes.View,
                    level: PrivilegeLevels.Global
                });
            }
        },
        {
            key: "canViewInfrastructure",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.Infrastructure,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditInfrastructure",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.Infrastructure,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canEditInfrastructureInReadOnly",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.Infrastructure,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewPlatformInfrastructure",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Infrastructure,
                    level: PrivilegeLevels.Global,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditPlatformInfrastructure",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Infrastructure,
                    level: PrivilegeLevels.Global,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewAllContent",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.ViewAllContent
                });
            }
        },
        {
            key: "canPreviewContent",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.MachineLearning,
                    targetDomain: PrivilegeDomains.AllowContentPreview
                });
            }
        },
        {
            key: "canPreviewPipeline",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.QueryPipelinePreview,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "isInternalUser",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.InternalUser
                });
            }
        },
        {
            key: "canEditSearchPages",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.SearchPages,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canEditAllSearchPages",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.SearchPages,
                    type: PrivilegeTypes.Edit,
                    targetId: PrivilegesConstants.allTargetIds
                });
            }
        },
        {
            key: "canCreateSearchPages",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.SearchPages,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canViewSearchPages",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.SearchPages,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canCreateExtensions",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Extensions,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canEditExtensions",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Extensions,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canEditAllExtensions",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Extensions,
                    type: PrivilegeTypes.Edit,
                    targetId: PrivilegesConstants.allTargetIds
                });
            }
        },
        {
            key: "canViewExtensions",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Extensions,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewConditions",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.QueryPipeline,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.AnalyticsData,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.CustomDimensions,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditAllConditions",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.QueryPipeline,
                    type: PrivilegeTypes.Edit
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.AnalyticsData,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.CustomDimensions,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewPipelines",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.QueryPipeline,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canCreatePipelines",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.QueryPipeline,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canEditPipelines",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.QueryPipeline,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canEditAllPipelines",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.QueryPipeline,
                    type: PrivilegeTypes.Edit,
                    targetId: PrivilegesConstants.allTargetIds
                });
            }
        },
        {
            key: "canEditSAML",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.SAMLIdentityProvider,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewSAML",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.SAMLIdentityProvider,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewActivities",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Activities,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewOrganizationTemplate",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.OrganizationTemplate,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewNotifications",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Subscription,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewTemporaryAccess",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.TemporaryAccess,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditNotifications",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Subscription,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canExecuteQuery",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.ExecuteQuery
                });
            }
        },
        {
            key: "canImpersonate",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.Impersonate
                });
            }
        },
        {
            key: "canViewCaseAssist",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Service,
                    targetDomain: PrivilegeDomains.CaseAssist,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditCaseAssist",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Service,
                    targetDomain: PrivilegeDomains.CaseAssist,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewInsightPanel",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Service,
                    targetDomain: PrivilegeDomains.InsightPanel,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditInsightPanel",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Service,
                    targetDomain: PrivilegeDomains.InsightPanel,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canEditInsightPanelInterface",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Service,
                    targetDomain: PrivilegeDomains.InsightPanelInterface,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewCatalogs",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Commerce,
                    targetDomain: PrivilegeDomains.Catalog,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditCatalogs",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Commerce,
                    targetDomain: PrivilegeDomains.Catalog,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewCommerceDataHealth",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Commerce,
                    targetDomain: PrivilegeDomains.CommerceDataHealth,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewMerchandisingHub",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Commerce,
                    targetDomain: PrivilegeDomains.MerchandisingHub,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditAllMerchandisingHub",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Commerce,
                    targetDomain: PrivilegeDomains.MerchandisingHub,
                    type: PrivilegeTypes.Edit,
                    targetId: PrivilegesConstants.allTargetIds
                });
            }
        },
        {
            key: "canViewMCPServerConfigurations",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.MachineLearning,
                    targetDomain: PrivilegeDomains.MCPServerConfigurations,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canCreateMCPServerConfigurations",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.MachineLearning,
                    targetDomain: PrivilegeDomains.MCPServerConfigurations,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canEditMCPServerConfigurations",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.MachineLearning,
                    targetDomain: PrivilegeDomains.MCPServerConfigurations,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewKnowledgeHub",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Knowledge,
                    targetDomain: PrivilegeDomains.KnowledgeHub
                });
            }
        },
        {
            key: "canViewAnswerConfigurations",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Knowledge,
                    targetDomain: PrivilegeDomains.AnswerManager,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditAnswerConfigurations",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Knowledge,
                    targetDomain: PrivilegeDomains.AnswerManager,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canUseChunkInspector",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Knowledge,
                    targetDomain: PrivilegeDomains.ChunkInspector
                });
            }
        },
        {
            key: "canViewGenAIAnalyticsIndexContent",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Knowledge,
                    targetDomain: PrivilegeDomains.GenAIAnalyticsIndexContent
                });
            }
        },
        {
            key: "canViewGenAIAnalytics",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Knowledge,
                    targetDomain: PrivilegeDomains.GenAIAnalytics
                });
            }
        },
        {
            key: "canViewAgentConfigurations",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Knowledge,
                    targetDomain: PrivilegeDomains.AgentConfiguration,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditAgentConfigurations",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Knowledge,
                    targetDomain: PrivilegeDomains.AgentConfiguration,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canCreateAgentConfigurations",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Knowledge,
                    targetDomain: PrivilegeDomains.AgentConfiguration,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canEditFields",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Field,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canCreateFields",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Field,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canViewFields",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Field,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canCreateResourceSnapshots",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ResourceSnapshots,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canEditResourceSnapshots",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ResourceSnapshots,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewResourceSnapshots",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.ResourceSnapshots,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewSearchHubs",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.SearchHubs,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditSearchHubs",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.SearchHubs,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewQueryLogs",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.QueryLogs,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewLogicalIndex",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.LogicalIndex,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditMachineLearningModels",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.MachineLearning,
                    targetDomain: PrivilegeDomains.MachineLearningModels,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canCreateMachineLearningModels",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.MachineLearning,
                    targetDomain: PrivilegeDomains.MachineLearningModels,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canViewClusters",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.OrganizationAdministration,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Infrastructure,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditClusters",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Organization,
                    type: PrivilegeTypes.View
                }) && this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Infrastructure,
                    type: PrivilegeTypes.View
                }) && this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.OrganizationAdministration,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewMachineLearningModels",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.MachineLearning,
                    targetDomain: PrivilegeDomains.MachineLearningModels,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewSnowflakeReaderAccounts",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.SnowflakeReaderAccounts,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditSnowflakeReaderAccounts",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.SnowflakeReaderAccounts,
                    type: PrivilegeTypes.Edit
                }) && this.canAdministrateUA;
            }
        },
        {
            key: "canViewSourceMetadata",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.SourceMetadata,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canReplayAnyQuery",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.SearchApi,
                    targetDomain: PrivilegeDomains.ReplayAnyQuery,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditTemporaryAccess",
            get: function get() {
                return this.hasWritePrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.TemporaryAccess,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewAnalyticsData",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.AnalyticsData,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canViewProperties",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.TrackingId,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditProperties",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.UsageAnalytics,
                    targetDomain: PrivilegeDomains.TrackingId,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canViewProjects",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Project,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditProjects",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Project,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canCreateProjects",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.Project,
                    type: PrivilegeTypes.Create
                });
            }
        },
        {
            key: "canViewCustomerKeys",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.CustomerKeys,
                    type: PrivilegeTypes.View
                });
            }
        },
        {
            key: "canEditCustomerKeys",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.CustomerKeys,
                    type: PrivilegeTypes.Edit
                });
            }
        },
        {
            key: "canCreateCustomerKeys",
            get: function get() {
                return this.hasPrivilege({
                    owner: PrivilegeOwners.Platform,
                    targetDomain: PrivilegeDomains.CustomerKeys,
                    type: PrivilegeTypes.Create
                });
            }
        }
    ]);
    return UserPrivilegesValidator;
}();

//# sourceMappingURL=UserPrivilegesValidator.js.map